import ClassNames from "classnames";
import { EmblaCarouselType } from "embla-carousel";
import { FunctionComponent, useState } from "react";
import { SlideImage } from "yet-another-react-lightbox";
import { selectActiveSite } from "../selectors/sites.js";
import { useAppSelector } from "../store/hooks.js";
import styles from "../stylesheets/components/image-gallery-layout-5.module.css";
import moduleBaseStyles from "../stylesheets/components/module-base.module.css";
import {
  ColorScheme,
  ContainerQueries,
  ImageModuleSettings,
  Language,
  TitleAndSubtitleTextAlign,
  TranslatedModule,
} from "../types/index.js";
import { css } from "../utils/emotion.js";
import { useDidMountEffect } from "../utils/hooks.js";
import {
  getGalleryMediaAspectRatio,
  getPlaceholderImage,
} from "../utils/utils.js";
import ModuleHeadings from "./ModuleHeadings.js";
import ImageModule from "./Modules/ImageModule.js";
import Slider from "./Slider.js";
import SliderArrow from "./SliderArrow.js";

interface Props {
  title: string;
  subtitle: string;
  textAlign: TitleAndSubtitleTextAlign;
  scheme: ColorScheme;
  isFirstOnPage: boolean;
  imageModules: TranslatedModule<ImageModuleSettings>[];
  queries: ContainerQueries;
  slides: SlideImage[];
  isPreview: boolean;
  languageId: Language;
  pageId: string;
  moduleId: string;
  activeModuleId: string | undefined;
  onClickImage: (index: number) => void;
}

const ImageGalleryLayout5: FunctionComponent<Props> = ({
  title,
  subtitle,
  textAlign,
  isFirstOnPage,
  scheme,
  imageModules,
  queries,
  slides,
  isPreview,
  languageId,
  pageId,
  moduleId,
  activeModuleId,
  onClickImage,
}) => {
  const [slider, setSlider] = useState<EmblaCarouselType>();
  const [slideIndex, setSlideIndex] = useState(0);
  const slidesToShow = !queries["Query--medium"] ? 1 : 3;

  const site = useAppSelector(selectActiveSite);

  useDidMountEffect(() => {
    if (!slider) return;

    const handleSlideChange = (slideApi: EmblaCarouselType) => {
      setSlideIndex(slideApi.selectedScrollSnap());
    };
    slider?.on("select", handleSlideChange);
    return () => {
      slider?.off("select", handleSlideChange);
    };
  }, [slider]);

  const images = imageModules.length
    ? imageModules
    : new Array(5)
        .fill(true)
        .map((_, index) =>
          getPlaceholderImage({
            siteId: site.id,
            pageId,
            languageId,
            parentId: moduleId,
            index,
          }),
        );

  return (
    <div className={styles.ImageGalleryLayout5}>
      <ModuleHeadings
        textAlign={textAlign}
        scheme={scheme}
        isFirstOnPage={isFirstOnPage}
        title={title}
        subtitle={subtitle}
        wrapperClassName={ClassNames(
          moduleBaseStyles.Module__Wrapper,
          moduleBaseStyles["Module__Wrapper--small-padding"],
          styles.ImageGalleryLayout5__Headings,
        )}
      />
      <div
        className={ClassNames(
          moduleBaseStyles.Module__Wrapper,
          moduleBaseStyles["Module__Wrapper--default-padding"],
          styles.ImageGalleryLayout5__Module__Wrapper,
        )}
      >
        <div className={styles.ImageGalleryLayout5__SliderWrapper}>
          <Slider
            containerClassName={styles.ImageGalleryLayout5__SliderContainer}
            slideClassName={ClassNames(styles.ImageGalleryLayout5__Slide, {
              [styles.ImageGalleryLayout5__SlideMobile]:
                !queries["Query--medium"],
            })}
            isPreview={isPreview}
            sliderEffect="slide"
            onSliderLoaded={setSlider}
            slidesToShow={slidesToShow}
            activePreviewSlideId={activeModuleId}
            slideIds={images.map(({ id }) => id)}
            loop={false}
          >
            {images.map((imageModule, index) => {
              const item = slides[index];
              return (
                <a
                  href={isPreview || !item ? undefined : item.src}
                  className={styles.ImageGalleryLayout5__Item}
                  key={imageModule.id}
                  onClick={(e) => {
                    e.preventDefault();
                    onClickImage(index);
                  }}
                >
                  <ImageModule
                    aspectRatio={getAspectRatio(index)}
                    sizes="50vw"
                    lazyLoad={true}
                    width={1920}
                    translatedModule={imageModule}
                    isPreview={isPreview}
                    isSlider={false}
                    pageId={pageId}
                    scheme={scheme}
                    showOverlay={false}
                  />
                </a>
              );
            })}
          </Slider>
          {slidesToShow < images.length && (
            <div>
              <SliderArrow
                className={ClassNames(styles.ImageGalleryLayout5__SliderArrow, {
                  [styles["ImageGalleryLayout5__SliderArrow--disabled"]]:
                    slideIndex === 0,
                })}
                iconClassName={ClassNames(
                  styles.ImageGalleryLayout5__SliderArrowIcon,
                  css({ fill: scheme.main.text }),
                )}
                glyph="arrow-back"
                direction="left"
                onClick={() => slider?.scrollPrev()}
                scheme={scheme}
                transparentBackground
                languageId={languageId}
              />
              <SliderArrow
                className={ClassNames(
                  styles.ImageGalleryLayout5__SliderArrow,
                  styles.ImageGalleryLayout5__SliderArrow_Next,
                  {
                    [styles["ImageGalleryLayout5__SliderArrow--disabled"]]:
                      slideIndex === images.length - slidesToShow,
                  },
                )}
                iconClassName={ClassNames(
                  styles.ImageGalleryLayout5__SliderArrowIcon,
                  css({ fill: scheme.main.text }),
                )}
                glyph="arrow-forward"
                direction="right"
                onClick={() => slider?.scrollNext()}
                scheme={scheme}
                transparentBackground
                languageId={languageId}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const getAspectRatio = (index: number): number =>
  getGalleryMediaAspectRatio(index, "layout-5");

export default ImageGalleryLayout5;
